import { ActivatedRoute } from '@angular/router';
import { CONFIG } from 'app/services/config.service';
import { DOCUMENT } from '@angular/common';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import {
  Inject,
  Injectable,
  PLATFORM_ID
} from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { SvcServices } from './svc.service';
import { GuidService } from './guid.service';
import { timeout, catchError, filter } from 'rxjs/operators';
import { AppConfigService } from '@citi-gcg-173875/citi-utils';
import { isPlatformBrowser } from '@angular/common';
import { E2eService } from './e2e.service';

const apiBase = '';

/**
 * The 'BackendService' handles the nuts and bolts of connecting to the Microapp.
 */
@Injectable()

export class BackendService {

  /**
   * A map that matches a simple endpoint like `/payments/submit` with the
   * correct (usually much longer) endpoint to use for the environment.
   */
  public endpoints: Map<string, string>;


  /**
   * The biz token to send as the authorization in each request's header. On
   * construction this is set by checking for a cookie parameter  
   */
   public bizToken: string;

  /**
   * The URL for connecting to API endpoints.
   */
  private url: string;
  public api: any;
  public blackBox: string;
  public devicePrint: string;
  public tmId: string;
  public DCLocation: any;

  /**
   * The internal method that connects to the API using the passed in arguments.
   *
   * If a full endpoint is defined in the environment variables, then that path/service will be
   * utilized here. Otherwise, the name of the service will be used as passed.
   *
   * Even if no other headers are to be used, each API call will be given the `uuid` header
   * containing a `datetime`. This is used by the microapp when determining whether or not to ping
   * the `keepalive` endpoint (used for maintaining session validity). The microapp will only call
   * `keepalive` on new interactions if it has been more than 5 minutes since the last call to the
   * `keepalive` service. This also allows for management of reverse proxy caching of API responses.
   *
   * [NB]: Logging the endpoint path for now, as there is a lot of testing around API integration.
   *
   * @param { string } method The HTTP verb to use in processing the request.
   * @param { string } endpoint The general name of the API endpoint to which the request is sent.
   * @param { any } body The request body.
   * @param { any } header Any necessary request header data.
   *
   * @return { Observable } Returns a promise for the complete HTTP response.
   */

  constructor(private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document,
    private httpClient: HttpClient,
    @Inject(CONFIG) private config,
    private guidService: GuidService,
    private _e2eService: E2eService,
    private _svcService: SvcServices,
    public _appConfigService: AppConfigService,
    @Inject(PLATFORM_ID) private platformId: string) {

    
    const { protocol, hostname, port } = document.location;

    var endpointMap;
    var config = this._appConfigService.getConfig("api");
    this.api = config;

    if (this.api.endpoints) {
      endpointMap =
        Object
          .keys(this.api.endpoints)
          .reduce((map, endpoint, i) => {
            map[i] = [endpoint, this.api.endpoints[endpoint]];
            return map;
          }, []);

      this.endpoints = new Map(endpointMap);
      console.log("isPlatformBrowser(this.platformId)", isPlatformBrowser(this.platformId))
      if (isPlatformBrowser(this.platformId)) {
        this.url = (this.api.host) ? `${this.api.host}${this.api.path}` : `${protocol}//${hostname}/${this.api.path}`;
      } else {
        console.log(" process.env.apiServerUrlPlatformServer="+process.env.apiServerUrlPlatformServer);
        console.log("this._appConfigService.getApiServerUrlPlatformServer(), ==", this._appConfigService.getApiServerUrlPlatformServer());
        if(process?.env?.apiServerUrlPlatformServer === "" || process?.env?.apiServerUrlPlatformServer === undefined || process?.env?.apiServerUrlPlatformServer === "undefined") {
          this.url = this._appConfigService.getApiServerUrlPlatformServer();
        } else {
          this.url = process.env.apiServerUrlPlatformServer;
        }
        //this.url = process?.env?.apiServerUrlPlatformServer &&  ? process?.env?.apiServerUrlPlatformServer : this._appConfigService.getApiServerUrlPlatformServer();
        console.log("this.url in Backendserver == "+ this.url);
      }
    } else {
      console.log('BackendService: endpoints error');
    }


  }

  private connect(method: string, endpoint: string, body: any, header: any, pathParams?: any, tmxEncryptionEnabled?:any): Observable<any> {
    let url;
    if (pathParams && Object.keys(pathParams).length > 0) {
      url = `${this.url}${(this.endpoints.get(endpoint) || endpoint)}`;
      url = this.urlReplace(url, pathParams);
    } else {
      url = `${this.url}${(this.endpoints.get(endpoint) || endpoint)}`;
    }

  if (this._svcService.isFusionChannel()) {
    let parsedEndpoint = this.parseEndpoint(method, this.endpoints.get(endpoint) || endpoint);
    if (this.tmId) {
      this.tmId = this._e2eService.enytmId(this.tmId, parsedEndpoint, tmxEncryptionEnabled);
    }
  }

    if(header == null){
      header = {};
    }
    let uuid = (header.uuid) ? header.uuid : this.guidService.getGuid();
    console.log(`Invoking url ${url} with uuid= ${uuid}`);
    const req = new HttpRequest(method, url, body, {
      headers: new HttpHeaders({
        'uuid': uuid,
        'Accept-Language': 'en_US',
        ...(this.api.headers || {}),
        ...(this.blackBox ? { blackBox: this.blackBox } : {}),
        ...(this.devicePrint ? { devicePrint: this.devicePrint } : {}),
        ...(this.bizToken ? { bizToken: this.bizToken } : {}),
        ...(this.tmId ? { tmxSessionId: this.tmId } : {}),
        ...(this.DCLocation ? { DCLocation: this.DCLocation } : {}),
        ...(/^patch|post|put|get|delete$/i.test(method) ? { 'Content-Type': 'application/json' } : {}),
        ...(header || {})
      }),
      responseType: 'json'
    });

    return this.httpClient
      .request(req)
      .pipe(
        // timeout(60000),
        catchError(this.handleError),
        filter(resp => resp instanceof HttpResponse)
      )

  }

  /**
   * Public method for HTTP DELETE connections to an endpoint
   *
   * @param { string } endpoint The path to the API service
   * @param { any } body The request body
   * @param { any } options Any necessary request header data
   *
   * @return { Observable } Returns the result of the private `connect()` method.
   */
  public delete(endpoint: string, body: any, options: any, pathParams?: any): Observable<any> {
    return this.connect('delete', endpoint, body, options, pathParams);
  }


  /**
   * Public method for HTTP GET connections to an endpoint
   *
   * @param { string } endpoint The path to the API service
   *
   * @return { Observable } Returns the result of the private `connect()` method.
   */
  public get(endpoint: string, body: any, options: any, pathParams?: any, tmxEncryptionEnabled?: any): Observable<any> {
    return this.connect('get', endpoint, body, options, pathParams, tmxEncryptionEnabled);
  }


  /**
   * Public method for HTTP POST connections to an endpoint
   *
   * @param { string } endpoint The path to the API service
   * @param { any } body The request body
   * @param { any } options Any necessary request header data
   *
   * @return { Observable } Returns the result of the private `connect()` method.
   */
  public post(endpoint: string, body: any, options: any, pathParams?: any,  tmxEncryptionEnabled?: any): Observable<any> {
    return this.connect('post', endpoint, body, options, pathParams, tmxEncryptionEnabled);
  }


  /**
   * Public method for HTTP PUT connections to an endpoint
   *
   * @param { string } endpoint The path to the API service
   * @param { any } body The request body
   * @param { any } options Any necessary request header data
   *
   * @return { Observable } Returns the result of the private `connect()` method.
   */
  public put(endpoint: string, body: any, options: any, pathParams?: any): Observable<any> {
    return this.connect('put', endpoint, body, options, pathParams);
  }

  /**
   *
   * @param {string} str
   * @param pathParams
   * @returns {string}
   */
  private urlReplace(str: string, pathParams: any) {
    return str.replace(
      /\{([a-z0-9A-Z]+)\}/g, (_, key) => pathParams[key]);
  }

  private handleError(error: HttpErrorResponse) {

    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong,
    console.error(
      `Backend returned code ====> ${error}, ` +
      `body is: ${JSON.stringify(error.error)}`);

    // return an observable with a user-facing error message
    return throwError(error);
  };

  private parseEndpoint(method: string, endpoint: string): string {
    if (endpoint && endpoint.startsWith('api')) {
      endpoint = endpoint.substr(3);
    }
    return method.toUpperCase() + endpoint;
  }

   //  connect Method for Quartz sso custom headers
   private connectFormURLEncoded(method: string, endpoint: string, body: any, header: any, pathParams?: any): Observable<any> {
    let url;
    if (pathParams && Object.keys(pathParams).length > 0) {
      url = `${this.url}${(this.endpoints.get(endpoint) || endpoint)}`;
      url = this.urlReplace(url, pathParams);
    } else {
      url = `${this.url}${(this.endpoints.get(endpoint) || endpoint)}`;
    }

    let uuid = (header.uuid) ? header.uuid : this.guidService.getGuid();
    console.log(`Invoking url ${url} with uuid= ${uuid}`);
    const req = new HttpRequest(method, url, body, {
      headers: new HttpHeaders({
        'uuid': uuid,
        'Accept-Language': 'en_US',
        ...(this.blackBox ? { blackBox: this.blackBox } : {}),
        ...(this.devicePrint ? { devicePrint: this.devicePrint } : {}),
        ...(this.tmId ? { tmxSessionId: this.tmId } : {}),
        ...(this.DCLocation ? { DCLocation: this.DCLocation } : {}),
        'channelId': 'QUARTZ',
        'countryCode':'US',
        'businessCode':'CRS',
        'Content-Type': 'application/x-www-form-urlencoded',
        'accept': 'application/json',
        'client_id':header.headers.client_id
      }),
      responseType: 'json'
    });
    return this.httpClient
      .request(req)
      .pipe(
        // timeout(60000),
        catchError(this.handleError),
        filter(resp => resp instanceof HttpResponse)
      )

  }

  public postFormURLEncoded(endpoint: string, body: any, options: any, pathParams?: any): Observable<any> {
    return this.connectFormURLEncoded('post', endpoint, body, options, pathParams);
  }

}